/*
 * PULL DOWN TO RELOAD, on the login screen. Asked for on 2026-10-01: "Can
 * we make it so dragging down on the login screen reloads the page?"
 *
 * The page never scrolls — the world fills it and the login screen is
 * fixed over the lot — so the browser's own pull to refresh has nothing to
 * pull, and a phone running the game from its home screen has none at all.
 * So the login screen does it itself: a finger (or the mouse) pressed
 * anywhere on it but its fields and buttons and dragged down brings a
 * reload mark down from the top with it, turning as it comes; past
 * PULL_RELOAD_AT pixels the mark lights and says so, and letting go there
 * reloads the page. Letting go short of it, or the drag going back up past
 * it, puts the mark away and nothing happens.
 *
 * The screen takes the vertical drag from the browser (touch-action in
 * style.css) so the two cannot both answer one gesture. It is kept in a
 * file of its own so the tests can run it without a page.
 */

/** How far down a drag must come, in pixels, before letting go reloads. */
export const PULL_RELOAD_AT = 80;
/** How far a press must move before it is a pull rather than a tap. */
const PULL_SLOP = 8;
/** The furthest the mark comes down, however far the drag goes. */
const PULL_MAX = 120;

/** Where the mark is for a drag dy pixels down: it follows at a damped pace and stops at PULL_MAX. */
export function pullOffset(dy: number): number {
  if (dy <= 0) return 0;
  return Math.min(PULL_MAX, dy * 0.6);
}

/** Whether letting go of a drag dy pixels down reloads. */
export function pullReady(dy: number): boolean {
  return dy >= PULL_RELOAD_AT;
}

/** What a pull must not start on: anything that is pressed or typed in for its own sake. */
const NOT_PULLED = "input, textarea, select, button, a, label";

/**
 * Wire the pull to a screen: `mark` is the reload mark it brings down,
 * `reload` what letting go past the line does (location.reload in the page).
 */
export function wirePullToReload(screen: HTMLElement, mark: HTMLElement, reload: () => void): void {
  let pointer: number | null = null;
  let x0 = 0;
  let y0 = 0;
  let dy = 0;
  let pulling = false;
  const show = () => {
    const off = pullOffset(dy);
    mark.style.transform = `translate(-50%, ${off}px) rotate(${Math.round((Math.min(dy, PULL_RELOAD_AT) / PULL_RELOAD_AT) * 270)}deg)`;
    mark.style.opacity = String(Math.min(1, off / 40));
    mark.classList.toggle("ready", pullReady(dy));
  };
  const stop = () => {
    pointer = null;
    pulling = false;
    dy = 0;
    screen.classList.remove("pulling");
    mark.classList.remove("ready");
    mark.style.transform = "";
    mark.style.opacity = "";
  };
  screen.addEventListener("pointerdown", (ev) => {
    if (pointer !== null || ev.button !== 0 || screen.scrollTop > 0) return;
    if ((ev.target as Element | null)?.closest?.(NOT_PULLED)) return;
    pointer = ev.pointerId;
    x0 = ev.clientX;
    y0 = ev.clientY;
    dy = 0;
  });
  screen.addEventListener("pointermove", (ev) => {
    if (ev.pointerId !== pointer) return;
    dy = ev.clientY - y0;
    if (!pulling) {
      if (dy < PULL_SLOP) {
        // Sideways or upwards first: that is not a pull, so let it be.
        if (Math.abs(ev.clientX - x0) > PULL_SLOP || dy < -PULL_SLOP) stop();
        return;
      }
      pulling = true;
      screen.classList.add("pulling");
      try {
        screen.setPointerCapture(ev.pointerId);
      } catch {
        /* the pointer is already gone; the up or cancel will follow */
      }
      window.getSelection?.()?.removeAllRanges();
    }
    show();
  });
  screen.addEventListener("pointerup", (ev) => {
    if (ev.pointerId !== pointer) return;
    const go = pulling && pullReady(dy);
    if (go) mark.classList.add("reloading");
    else stop();
    if (go) reload();
  });
  // Nothing on the screen is dragged off it while a pull may be starting:
  // the browser's own drag of a picture or a selection would cancel it.
  screen.addEventListener("dragstart", (ev) => {
    if (pointer !== null) ev.preventDefault();
  });
  screen.addEventListener("pointercancel", (ev) => {
    if (ev.pointerId === pointer) stop();
  });
}
